<template>
  <div class="gm-detail-pane">
    <div class="gm-detail-hero">
      <div class="gm-detail-hero__top">
        <button class="gm-detail-hero__back" @click="$emit('close')" v-b-tooltip.hover :title="$t('inbox.backToInbox')">
          <b-icon icon="arrow-left" />
        </button>
        <div class="gm-detail-hero__actions">
          <button v-if="canSend" class="gm-detail-hero__btn" @click="openInlineReply" v-b-tooltip.hover :title="$t('inbox.reply')"><b-icon icon="reply" /></button>
          <button v-if="canSend" class="gm-detail-hero__btn" @click="$emit('forward')" v-b-tooltip.hover :title="$t('inbox.forward')"><b-icon icon="forward" /></button>
          <button class="gm-detail-hero__btn" @click="$emit('toggle-read')" v-b-tooltip.hover :title="email.is_read ? $t('inbox.markAsUnread') : $t('inbox.markAsRead')">
            <b-icon :icon="email.is_read ? 'envelope' : 'envelope-open'" />
          </button>
          <button
            v-if="currentFolder === 'Spam'"
            class="gm-detail-hero__btn gm-detail-hero__btn--restore"
            @click="$emit('move-to', 'INBOX')"
            v-b-tooltip.hover :title="$t('inbox.unmarkAsSpam')"
          ><b-icon icon="shield-check" /></button>
          <button
            v-if="currentFolder === 'Trash'"
            class="gm-detail-hero__btn gm-detail-hero__btn--restore"
            @click="$emit('move-to', 'INBOX')"
            v-b-tooltip.hover :title="$t('inbox.restore')"
          ><b-icon icon="arrow-counterclockwise" /></button>
          <button v-if="canDelete" class="gm-detail-hero__btn gm-detail-hero__btn--danger" @click="$emit('delete')" v-b-tooltip.hover :title="$t('inbox.delete')"><b-icon icon="trash" /></button>
        </div>
      </div>
      <div class="gm-detail-hero__subject">
        <h2>{{ email.subject || '(no subject)' }}
          <span v-if="threadMessages.length > 1" class="gm-thread-count-badge">{{ threadMessages.length }}</span>
        </h2>
        <button class="gm-detail-hero__star" @click="$emit('toggle-star')" v-b-tooltip.hover :title="email.is_flagged ? $t('inbox.unstar') : $t('inbox.star')">
          <b-icon :icon="email.is_flagged ? 'star-fill' : 'star'" :class="email.is_flagged ? 'gm-star--on' : 'gm-star--off'" />
        </button>
      </div>
      <div class="gm-detail-hero__meta">
        <div class="gm-detail-avatar gm-detail-avatar--lg">{{ (email.from.name || email.from.address || '?')[0].toUpperCase() }}</div>
        <div class="gm-detail-meta-body">
          <div class="gm-detail-from">
            <strong>{{ email.from.name || email.from.address }}</strong>
            <span class="gm-detail-from-addr" v-if="email.from.name">&lt;{{ email.from.address }}&gt;</span>
          </div>
          <div class="gm-detail-to">to {{ (email.to || []).map(t => t.name || t.address).join(', ') }}</div>
        </div>
        <div class="gm-detail-date">
          {{ formatDate(email.received_at, 'long') }},
          {{ formatDate(email.received_at, 'time') }}
        </div>
      </div>
    </div>

    <div class="gm-detail-body">
      <!-- Loading state while fetching full thread content -->
      <div v-if="loadingThread" class="gm-detail-loading">
        <b-spinner small variant="primary" />
        <span>{{ $t('inbox.downloadingMessage') }}</span>
      </div>

      <template v-else>
        <!-- Thread: collapsed earlier messages -->
        <template v-if="olderThreadMessages.length > 0">
          <div
            v-for="(msg, idx) in olderThreadMessages"
            :key="msg.id || idx"
            class="gm-thread-msg"
            :class="{ 'gm-thread-msg--expanded': expandedThreadIds.has(msg.id) }"
          >
            <div class="gm-thread-msg__header" @click="toggleThreadMsg(msg.id)">
              <div class="gm-thread-msg__avatar">{{ (msg.from.name || msg.from.address || '?')[0].toUpperCase() }}</div>
              <div class="gm-thread-msg__meta">
                <strong class="gm-thread-msg__sender">{{ msg.from.name || msg.from.address }}</strong>
                <span class="gm-thread-msg__snippet" v-if="!expandedThreadIds.has(msg.id)">{{ threadSnippet(msg) }}</span>
              </div>
              <span class="gm-thread-msg__date">{{ formatDate(msg.received_at, 'short') }}</span>
              <b-icon :icon="expandedThreadIds.has(msg.id) ? 'chevron-up' : 'chevron-down'" class="gm-thread-msg__toggle" />
            </div>
            <div v-if="expandedThreadIds.has(msg.id)" class="gm-thread-msg__body">
              <iframe
                class="gm-detail-iframe"
                sandbox="allow-same-origin allow-popups"
                :srcdoc="buildHtmlDoc(msg)"
                @load="resizeThreadFrame($event)"
              />
            </div>
          </div>
          <div class="gm-thread-divider"></div>
        </template>

        <!-- Current / latest email body -->
        <iframe
          v-if="email.html_body || email.body"
          ref="emailFrame"
          class="gm-detail-iframe"
          sandbox="allow-same-origin allow-popups"
          :srcdoc="emailHtmlDoc"
          @load="resizeFrame"
        />
        <div v-else class="gm-detail-nobody">
          <b-icon icon="envelope" style="font-size:2rem;opacity:.2" />
          <div>{{ $t('inbox.noBody') }}</div>
        </div>
      </template>
    </div>

    <div v-if="email.attachments && email.attachments.length" class="gm-attachments">
      <div class="gm-attachments__title">
        <b-icon icon="paperclip" class="mr-1" /> {{ $tc('inbox.attachments', email.attachments.length, { count: email.attachments.length }) }}
      </div>
      <div class="gm-attachments__list">
        <div
          v-for="(att, idx) in email.attachments"
          :key="idx"
          class="gm-attachment-card"
          @click="$emit('download-attachment', att)"
        >
          <img
            v-if="att.content_type && att.content_type.startsWith('image/')"
            :src="attachmentDataUrl(att)"
            class="gm-attachment-thumb"
          />
          <b-icon v-else icon="file-earmark" class="gm-attachment-icon" />
          <div class="gm-attachment-info">
            <div class="gm-attachment-name">{{ att.filename || 'attachment' }}</div>
            <div class="gm-attachment-size">{{ formatFileSize(att.size) }}</div>
          </div>
          <b-icon icon="download" class="gm-attachment-dl" />
        </div>
      </div>
    </div>

    <!-- Inline reply editor (Gmail-style) -->
    <div v-if="inlineReply && canSend" class="gm-inline-reply">
      <div class="gm-inline-reply__header">
        <div class="gm-inline-reply__avatar">{{ (selectedAccount && selectedAccount.email || '?')[0].toUpperCase() }}</div>
        <div class="gm-inline-reply__meta">
          <span class="gm-inline-reply__label">{{ $t('inbox.reply') }}</span>
          <span class="gm-inline-reply__to">{{ email.from.name || email.from.address }}</span>
        </div>
        <button class="gm-toolbar-btn" @click="closeInlineReply" v-b-tooltip.hover :title="$t('inbox.discard')">
          <b-icon icon="x-lg" />
        </button>
      </div>

      <div class="gm-inline-reply__fields" v-if="showInlineCc">
        <div class="gm-inline-reply__field-row">
          <span class="gm-inline-reply__field-label">{{ $t('inbox.cc') }}</span>
          <input v-model="inlineCc" type="email" placeholder="cc@example.com" class="gm-inline-reply__input" />
        </div>
      </div>

      <div class="gm-inline-reply__toolbar">
        <button class="compose-tool" @click.prevent="inlineExecCmd('bold')" :title="$t('inbox.bold')"><b>B</b></button>
        <button class="compose-tool" @click.prevent="inlineExecCmd('italic')" :title="$t('inbox.italic')"><i>I</i></button>
        <button class="compose-tool" @click.prevent="inlineExecCmd('underline')" :title="$t('inbox.underline')"><u>U</u></button>
        <span class="compose-tool-sep" />
        <button class="compose-tool" @click.prevent="inlineExecCmd('insertUnorderedList')" :title="$t('inbox.bulletList')">•≡</button>
        <button class="compose-tool" @click.prevent="inlineExecCmd('insertOrderedList')" :title="$t('inbox.numberedList')">1≡</button>
        <span class="compose-tool-sep" />
        <button class="compose-tool" @click.prevent="inlineInsertLink" :title="$t('inbox.insertLink')">🔗</button>
        <span class="compose-tool-sep" />
        <button class="compose-tool" @click.prevent="$refs.inlineFileInput.click()" :title="$t('inbox.attachFile')"><b-icon icon="paperclip" /></button>
        <input ref="inlineFileInput" type="file" multiple style="display:none" @change="onInlineFilesSelected" />
        <span class="compose-tool-sep" />
        <button class="compose-tool" @click="showInlineCc = !showInlineCc">Cc</button>
      </div>

      <div v-if="inlineAttachments.length" class="gm-inline-reply__attachments">
        <div v-for="(f, i) in inlineAttachments" :key="i" class="compose-att-chip">
          <b-icon :icon="f.type.startsWith('image/') ? 'image' : 'file-earmark'" class="mr-1" />
          <span class="compose-att-name">{{ f.name }}</span>
          <span class="compose-att-size">({{ formatFileSize(f.size) }})</span>
          <button class="compose-att-remove" @click="inlineAttachments.splice(i, 1)">&times;</button>
        </div>
      </div>

      <div
        ref="inlineReplyBody"
        class="gm-inline-reply__body"
        contenteditable="true"
        :data-placeholder="$t('inbox.writeMessage')"
      />

      <div class="gm-inline-reply__footer">
        <button class="compose-send-btn" :disabled="inlineSending" @click="sendInlineReply">
          <b-spinner small v-if="inlineSending" style="width:14px;height:14px;flex-shrink:0" />
          <svg v-else viewBox="0 0 16 16" width="14" height="14" fill="currentColor" style="flex-shrink:0"><path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/></svg>
          {{ $t('inbox.send') }}
        </button>
        <button class="compose-discard-btn" @click="closeInlineReply">{{ $t('inbox.discard') }}</button>
        <button class="gm-inline-reply__popout" @click="popOutReply" v-b-tooltip.hover :title="$t('inbox.maximize')">
          <b-icon icon="arrows-fullscreen" />
        </button>
      </div>
    </div>

    <!-- Reply prompt bar (shown when inline reply is not open) -->
    <div v-if="canSend && !inlineReply" class="gm-reply-bar" @click="openInlineReply">
      <div class="gm-reply-bar__prompt">
        <b-icon icon="reply" class="mr-2" />
        {{ $t('inbox.replyTo') }}&nbsp;<strong>{{ email.from.name || email.from.address }}</strong>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InboxEmailDetail',
  props: {
    email: { type: Object, required: true },
    threadMessages: { type: Array, default: () => [] },
    loadingThread: { type: Boolean, default: false },
    currentFolder: { type: String, required: true },
    selectedAccount: { type: Object, default: null },
    inlineSending: { type: Boolean, default: false },
    canSend: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true }
  },
  data() {
    return {
      expandedThreadIds: new Set(),
      inlineReply: false,
      inlineCc: '',
      showInlineCc: false,
      inlineAttachments: []
    };
  },
  watch: {
    email() {
      this.expandedThreadIds = new Set();
      this.closeInlineReply();
    }
  },
  computed: {
    olderThreadMessages() {
      if (this.threadMessages.length <= 1) return [];
      return this.threadMessages.filter(m => m.id !== this.email.id);
    },
    emailHtmlDoc() {
      if (!this.email) return '';
      const html  = this.email.html_body;
      const plain = this.email.body;

      const hasOwnBackground = (h) => {
        if (/<(?:body|html|table)[^>]*\sbgcolor\s*=/i.test(h)) return true;
        if (/<(?:body|html)[^>]*\sstyle\s*=\s*"[^"]*background/i.test(h)) return true;
        const styleBlocks = h.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
        if (styleBlocks) {
          for (const block of styleBlocks) {
            const content = block.replace(/<\/?style[^>]*>/gi, '');
            if (/(?:body|html)\s*\{[^}]*background/i.test(content)) return true;
          }
        }
        return false;
      };

      const minimalBase = `<style>
        *{box-sizing:border-box}
        html,body{margin:0;padding:0}
        body{padding:16px 20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}
        img{max-width:100%;height:auto}
        table{border-collapse:collapse;max-width:100%}
      </style>`;

      const darkBase = `<style>
        *{box-sizing:border-box}
        html,body{margin:0;padding:0;background:#2a2d35;color:#d1d5db}
        body{padding:16px 20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}
        img{max-width:100%;height:auto}
        table{border-collapse:collapse;max-width:100%}
        a{color:#60a5fa;text-decoration:none}
        a:hover{text-decoration:underline}
        strong{color:#e5e7eb;font-weight:600}
        hr{border:none;border-top:1px solid #3f4350;margin:1em 0}
      </style>`;

      const plainBase = `<style>
        html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.75;color:#d1d5db;background:#2a2d35}
        body{padding:20px 24px}
        pre{margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.75}
        a{color:#60a5fa;text-decoration:none}
        a:hover{text-decoration:underline}
        strong{color:#e5e7eb;font-weight:600}
        hr{border:none;border-top:1px solid #3f4350;margin:1em 0}
      </style>`;

      if (html) {
        const styled = hasOwnBackground(html);
        const base = styled ? minimalBase : darkBase;
        if (/^<!doctype|^<html/i.test(html.trim())) {
          if (/<head([^>]*)>/i.test(html)) {
            return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
          }
          return html.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
        }
        return `<!DOCTYPE html><html><head><meta charset="utf-8">${base}</head><body>${html}</body></html>`;
      }
      const esc = plain
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const formatted = esc
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
        .replace(/^---+\s*$/gm, '<hr>')
        .replace(/(https?:\/\/[^\s)<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      return `<!DOCTYPE html><html><head><meta charset="utf-8">${plainBase}</head><body><pre style="margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.65">${formatted}</pre></body></html>`;
    }
  },
  methods: {
    resizeFrame() {
      this.$nextTick(() => {
        const frame = this.$refs.emailFrame;
        if (frame && frame.contentDocument) {
          const body = frame.contentDocument.body;
          if (body) {
            frame.style.height = body.scrollHeight + 'px';
          }
        }
      });
    },
    toggleThreadMsg(id) {
      const s = new Set(this.expandedThreadIds);
      if (s.has(id)) s.delete(id); else s.add(id);
      this.expandedThreadIds = s;
    },
    threadSnippet(msg) {
      const text = msg.body || msg.html_body || '';
      return text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim().substring(0, 80);
    },
    buildHtmlDoc(msg) {
      const html = msg.html_body;
      const plain = msg.body;

      const hasOwnBg = (h) => {
        if (/<(?:body|html|table)[^>]*\sbgcolor\s*=/i.test(h)) return true;
        if (/<(?:body|html)[^>]*\sstyle\s*=\s*"[^"]*background/i.test(h)) return true;
        return false;
      };

      const minimalBase = `<style>*{box-sizing:border-box}html,body{margin:0;padding:0}body{padding:12px 16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}img{max-width:100%;height:auto}table{border-collapse:collapse;max-width:100%}</style>`;
      const darkBase = `<style>*{box-sizing:border-box}html,body{margin:0;padding:0;background:#2a2d35;color:#d1d5db}body{padding:12px 16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}img{max-width:100%;height:auto}table{border-collapse:collapse;max-width:100%}a{color:#60a5fa;text-decoration:none}a:hover{text-decoration:underline}strong{color:#e5e7eb;font-weight:600}hr{border:none;border-top:1px solid #3f4350;margin:1em 0}</style>`;
      const plainBase = `<style>html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.75;color:#d1d5db;background:#2a2d35}body{padding:12px 16px}pre{margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.75}a{color:#60a5fa}strong{color:#e5e7eb}hr{border:none;border-top:1px solid #3f4350;margin:1em 0}</style>`;

      if (html) {
        const styled = hasOwnBg(html);
        const base = styled ? minimalBase : darkBase;
        if (/^<!doctype|^<html/i.test(html.trim())) {
          if (/<head([^>]*)>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
          return html.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
        }
        return `<!DOCTYPE html><html><head><meta charset="utf-8">${base}</head><body>${html}</body></html>`;
      }
      const esc = (plain || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const formatted = esc
        .replace(/(https?:\/\/[^\s)<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      return `<!DOCTYPE html><html><head><meta charset="utf-8">${plainBase}</head><body><pre style="margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.65">${formatted}</pre></body></html>`;
    },
    resizeThreadFrame(event) {
      this.$nextTick(() => {
        const frame = event.target;
        if (frame && frame.contentDocument && frame.contentDocument.body) {
          frame.style.height = frame.contentDocument.body.scrollHeight + 'px';
        }
      });
    },
    formatDate(dateStr, format = 'long') {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (format === 'time') {
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      }
      if (format === 'short') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      if (format === 'long') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      return d.toLocaleDateString();
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
    attachmentDataUrl(att) {
      if (!att.data) return '';
      const b64 = typeof att.data === 'string' ? att.data : btoa(String.fromCharCode(...new Uint8Array(att.data)));
      return `data:${att.content_type || 'application/octet-stream'};base64,${b64}`;
    },

    openInlineReply() {
      this.inlineReply = true;
      this.inlineCc = '';
      this.showInlineCc = false;
      this.inlineAttachments = [];
      this.$nextTick(() => {
        const body = this.$refs.inlineReplyBody;
        if (body) {
          const senderName = (this.email.from && this.email.from.name) || (this.email.from && this.email.from.address) || '';
          const senderAddr = (this.email.from && this.email.from.address) || '';
          const dateStr = this.email.received_at ? new Date(this.email.received_at).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
          const quoted = this.email.html_body || (this.email.body || '').replace(/\n/g, '<br>');
          body.innerHTML = `<div class="compose-reply-area"><br></div><div class="compose-quoted" contenteditable="false" style="margin-top:1rem"><div style="color:#6b7280;font-size:0.8125rem;margin-bottom:0.5rem">On ${dateStr}, ${senderName} &lt;${senderAddr}&gt; wrote:</div><blockquote style="border-left:2px solid #6c80ff;margin:0 0 0 0.5rem;padding-left:0.75rem;color:#9ca3af">${quoted}</blockquote></div>`;
          const replyArea = body.querySelector('.compose-reply-area');
          if (replyArea) {
            body.focus();
            const sel = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(replyArea);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
        const pane = this.$el;
        if (pane) pane.scrollTop = pane.scrollHeight;
      });
    },

    closeInlineReply() {
      this.inlineReply = false;
      this.inlineCc = '';
      this.showInlineCc = false;
      this.inlineAttachments = [];
    },

    sendInlineReply() {
      const body = this.$refs.inlineReplyBody;
      const htmlBody = body ? body.innerHTML : '';
      const senderAddr = (this.email.from && this.email.from.address) || '';
      const refs = [...(this.email.references || [])];
      if (this.email.message_id && !refs.includes(this.email.message_id)) refs.push(this.email.message_id);
      this.$emit('inline-reply-send', {
        to: senderAddr,
        cc: this.inlineCc,
        subject: this.email.subject ? (this.email.subject.startsWith('Re:') ? this.email.subject : `Re: ${this.email.subject}`) : '',
        htmlBody,
        inReplyTo: this.email.message_id || '',
        references: refs,
        attachments: this.inlineAttachments
      });
    },

    popOutReply() {
      const body = this.$refs.inlineReplyBody;
      const htmlBody = body ? body.innerHTML : '';
      const senderAddr = (this.email.from && this.email.from.address) || '';
      const refs = [...(this.email.references || [])];
      if (this.email.message_id && !refs.includes(this.email.message_id)) refs.push(this.email.message_id);
      this.closeInlineReply();
      this.$emit('reply-popout', {
        title: this.$t('inbox.reply'),
        to: senderAddr,
        subject: this.email.subject ? (this.email.subject.startsWith('Re:') ? this.email.subject : `Re: ${this.email.subject}`) : '',
        bodyHtml: htmlBody,
        inReplyTo: this.email.message_id || '',
        references: refs,
      });
    },

    inlineExecCmd(cmd, value = null) {
      const body = this.$refs.inlineReplyBody;
      if (body) body.focus();
      document.execCommand(cmd, false, value);
    },

    inlineInsertLink() {
      const url = window.prompt('Enter URL:', 'https://');
      if (url) this.inlineExecCmd('createLink', url);
    },

    onInlineFilesSelected(e) {
      const files = Array.from(e.target.files);
      this.inlineAttachments.push(...files);
      e.target.value = '';
    }
  }
};
</script>

<style scoped>
.gm-detail-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary, #16171d);
  overflow: hidden;
}

.gm-detail-hero {
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  padding: 0.625rem 1rem 0.75rem;
}

.gm-detail-hero__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.gm-detail-hero__back {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
}
.gm-detail-hero__back:hover {
  background: rgba(255,255,255,0.25);
}

.gm-detail-hero__actions {
  display: flex;
  gap: 0.375rem;
}

.gm-detail-hero__btn {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
}
.gm-detail-hero__btn:hover {
  background: rgba(255,255,255,0.25);
}
.gm-detail-hero__btn--restore:hover {
  background: rgba(34, 197, 94, 0.3);
}
.gm-detail-hero__btn--danger:hover {
  background: rgba(239, 68, 68, 0.3);
}

.gm-detail-hero__subject {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.gm-detail-hero__subject h2 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gm-detail-hero__star {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
  flex-shrink: 0;
  margin-left: 0.75rem;
}
.gm-detail-hero__star:hover {
  background: rgba(255,255,255,0.25);
}

.gm-star--on {
  color: #fbbf24;
}
.gm-star--off {
  color: rgba(255,255,255,0.6);
}

.gm-detail-hero__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-detail-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
  text-transform: uppercase;
}

.gm-detail-meta-body {
  flex: 1;
  min-width: 0;
}

.gm-detail-from {
  font-size: 0.8125rem;
  margin-bottom: 0.0625rem;
}

.gm-detail-from strong {
  font-weight: 600;
}

.gm-detail-from-addr {
  opacity: 0.8;
  margin-left: 0.25rem;
}

.gm-detail-to {
  font-size: 0.75rem;
  opacity: 0.8;
}

.gm-detail-date {
  font-size: 0.75rem;
  opacity: 0.8;
  text-align: right;
  flex-shrink: 0;
}

.gm-detail-body {
  flex: 1;
  min-height: 0;
  padding: 1.25rem;
  overflow-y: auto;
  position: relative;
}

.gm-detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 2rem 1rem;
  color: var(--text-muted, #6b7280);
  font-size: 0.875rem;
}

.gm-detail-iframe {
  width: 100%;
  border: none;
  background: transparent;
  display: block;
  min-height: 200px;
}

.gm-detail-nobody {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-muted, #6b7280);
  text-align: center;
}

.gm-attachments {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-separator, #2a2d35);
  background: var(--bg-secondary, #1e1f26);
}

.gm-attachments__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #e5e7eb);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.gm-attachments__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.gm-attachment-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.gm-attachment-card:hover {
  background: var(--hover-bg, rgba(255,255,255,0.02));
  border-color: var(--primary-start, #6c80ff);
}

.gm-attachment-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.gm-attachment-icon {
  font-size: 1.5rem;
  color: var(--text-muted, #6b7280);
  flex-shrink: 0;
}

.gm-attachment-info {
  flex: 1;
  min-width: 0;
}

.gm-attachment-name {
  font-size: 0.875rem;
  color: var(--text-primary, #e5e7eb);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-attachment-size {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
}

.gm-attachment-dl {
  font-size: 0.875rem;
  color: var(--text-muted, #6b7280);
  opacity: 0;
  transition: opacity 0.12s;
  flex-shrink: 0;
}
.gm-attachment-card:hover .gm-attachment-dl {
  opacity: 1;
}

.gm-reply-bar {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-separator, #2a2d35);
  background: var(--bg-secondary, #1e1f26);
  cursor: pointer;
}

.gm-reply-bar__prompt {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-secondary, #9ca3af);
  transition: border-color 0.12s;
}
.gm-reply-bar:hover .gm-reply-bar__prompt {
  border-color: var(--primary-start, #6c80ff);
}

/* ── Thread messages ── */
.gm-thread-count-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  background: rgba(255,255,255,0.2);
  color: #fff;
  border-radius: 4px;
  padding: 0 5px;
  line-height: 18px;
  vertical-align: middle;
  margin-left: 0.375rem;
}

.gm-thread-msg {
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  background: var(--bg-secondary, #1e1f26);
  overflow: hidden;
  transition: border-color 0.12s;
}
.gm-thread-msg:hover {
  border-color: var(--text-muted, #6b7280);
}
.gm-thread-msg--expanded {
  border-color: var(--primary-start, #6c80ff);
}

.gm-thread-msg__header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  user-select: none;
}

.gm-thread-msg__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff), var(--primary-end, #764ba2));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.gm-thread-msg__meta {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.gm-thread-msg__sender {
  font-size: 0.8125rem;
  color: var(--text-primary, #e5e7eb);
  white-space: nowrap;
  flex-shrink: 0;
}

.gm-thread-msg__snippet {
  font-size: 0.8125rem;
  color: var(--text-muted, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.gm-thread-msg__date {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
  white-space: nowrap;
  flex-shrink: 0;
}

.gm-thread-msg__toggle {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
  flex-shrink: 0;
}

.gm-thread-msg__body {
  border-top: 1px solid var(--border-separator, #2a2d35);
  padding: 0.75rem;
}
.gm-thread-msg__body .gm-detail-iframe {
  min-height: 60px;
}

.gm-thread-divider {
  height: 1px;
  background: var(--border-separator, #2a2d35);
  margin: 0.75rem 0 1rem;
}

/* ── Inline reply editor ── */

/* Shared compose styles (also used in InboxComposePanel) */
.compose-tool {
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.12s, color 0.12s;
}
.compose-tool:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}
.compose-tool-sep {
  width: 1px;
  height: 20px;
  background: var(--border-separator, #2a2d35);
  margin: 0 0.25rem;
}
.compose-att-chip {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-primary, #e5e7eb);
}
.compose-att-name {
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.compose-att-size {
  color: var(--text-muted, #6b7280);
  font-size: 0.75rem;
}
.compose-att-remove {
  background: none;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0;
  margin-left: 0.25rem;
  transition: color 0.12s;
}
.compose-att-remove:hover {
  color: #ef4444;
}
.compose-send-btn {
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.12s, box-shadow 0.12s;
}
.compose-send-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}
.compose-send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.compose-discard-btn {
  background: transparent;
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-secondary, #9ca3af);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.compose-discard-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  border-color: #ef4444;
  color: #ef4444;
}

.gm-inline-reply {
  margin: 1rem 1.25rem;
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 8px;
  background: var(--bg-secondary, #1e1f26);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.gm-inline-reply__header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
}

.gm-inline-reply__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff), var(--primary-end, #764ba2));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  flex-shrink: 0;
}

.gm-inline-reply__meta {
  flex: 1;
  min-width: 0;
}

.gm-inline-reply__label {
  font-size: 0.8125rem;
  color: var(--text-muted, #6b7280);
  margin-right: 0.375rem;
}

.gm-inline-reply__to {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary, #e5e7eb);
}

.gm-inline-reply__header .gm-toolbar-btn {
  background: transparent;
  border: none;
  color: var(--text-muted, #6b7280);
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.gm-inline-reply__header .gm-toolbar-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}

.gm-inline-reply__fields {
  padding: 0.5rem 1rem 0;
}

.gm-inline-reply__field-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.gm-inline-reply__field-label {
  font-size: 0.875rem;
  color: var(--text-secondary, #9ca3af);
  width: 30px;
  flex-shrink: 0;
}

.gm-inline-reply__input {
  flex: 1;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-primary, #e5e7eb);
  border-radius: 4px;
  padding: 0.375rem 0.625rem;
  font-size: 0.875rem;
}
.gm-inline-reply__input:focus {
  outline: none;
  border-color: var(--primary-start, #6c80ff);
}

.gm-inline-reply__toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 1rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
}

.gm-inline-reply__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
}

.gm-inline-reply__body {
  padding: 0.75rem 1rem;
  min-height: 150px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-primary, #e5e7eb);
  outline: none;
}
.gm-inline-reply__body:empty:before {
  content: attr(data-placeholder);
  color: var(--text-muted, #6b7280);
  pointer-events: none;
}

.gm-inline-reply__footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--border-separator, #2a2d35);
}

.gm-inline-reply__popout {
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--text-muted, #6b7280);
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.gm-inline-reply__popout:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}
</style>

